import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Stack,
  Divider,
  TextField,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  useTheme,
} from '@mui/material'
import {
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Translate as TranslateIcon,
  Palette as PaletteIcon,
  Person as PersonIcon,
} from '@mui/icons-material'
import { useAuthStore } from '../store/authStore'
import { useThemeMode } from '../theme/ThemeContext'
import { useSettingsStore, LANGUAGES, type Language } from '../store/settingsStore'
import { useLocalizedLabel } from '../utils/getLocalizedLabel'
import { useToast } from '../components/useToast'
import ConfirmDialog from '../components/ConfirmDialog'
import { userService } from '../api/adminServices'
import { useEnumStore } from '../store/enumStore'

function ProfileRow({ label, value }: { label: string; value?: string }) {
  const { t } = useTranslation()
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500} sx={{ textAlign: 'end' }}>
        {value || t('settings.notProvided')}
      </Typography>
    </Stack>
  )
}

export default function SettingsPage() {
  const theme = useTheme()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { user, logout } = useAuthStore()
  const { mode, setMode } = useThemeMode()
  const language = useSettingsStore((s) => s.language)
  const setLanguage = useSettingsStore((s) => s.setLanguage)
  const getLocalizedLabel = useLocalizedLabel()
  const toast = useToast()
  const clearEnums = useEnumStore((s) => s.clearEnums)

  const [pendingLanguage, setPendingLanguage] = useState<Language | null>(null)
  const [languageChangeLoading, setLanguageChangeLoading] = useState(false)

  const handleLanguageSelect = (next: Language) => {
    if (next === language) return
    setPendingLanguage(next)
  }

  const handleLanguageChangeConfirm = async () => {
    if (!pendingLanguage || !user?.id) {
      setPendingLanguage(null)
      return
    }

    setLanguageChangeLoading(true)
    try {
      await userService.update(user.id, { locale: pendingLanguage })
      setLanguage(pendingLanguage)
      clearEnums()
      logout()
      setPendingLanguage(null)
      navigate('/login')
    } catch (err) {
      toast.errorFrom(err, t('settings.languageChangeFailed'))
    } finally {
      setLanguageChangeLoading(false)
    }
  }

  const handleThemeChange = (next: 'light' | 'dark') => {
    setMode(next)
    toast.success(t('settings.themeChanged'))
  }

  const pendingLanguageLabel =
    pendingLanguage != null
      ? LANGUAGES.find((l) => l.code === pendingLanguage)?.nativeLabel ?? pendingLanguage
      : ''

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5" fontWeight={700}>
        {t('settings.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>
        {t('settings.subtitle')}
      </Typography>

      {/* Profile */}
      <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3, mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <PersonIcon color="primary" fontSize="small" />
            <Typography variant="subtitle1" fontWeight={600}>
              {t('settings.profileSection')}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Avatar sx={{ width: 56, height: 56, bgcolor: theme.palette.primary.main, fontSize: '1.25rem' }}>
              {user?.name?.charAt(0).toUpperCase() || 'A'}
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                {user?.name || 'Admin User'}
              </Typography>
              <Chip
                size="small"
                label={user?.status ? getLocalizedLabel('userStatus', user.status) : '—'}
                color={user?.status === 'ACTIVE' ? 'success' : 'default'}
                sx={{ mt: 0.5 }}
              />
            </Box>
          </Stack>
          <Divider />
          <ProfileRow label={t('settings.email')} value={user?.email} />
          <Divider />
          <ProfileRow label={t('settings.phone')} value={user?.phone} />
          <Divider />
          <ProfileRow
            label={t('settings.role')}
            value={user?.roles?.[0] ? getLocalizedLabel('userRole', user.roles[0]) : undefined}
          />
          <Divider />
          <ProfileRow label={t('settings.accountId')} value={user?.accountId} />
        </CardContent>
      </Card>

      {/* Preferences */}
      <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <PaletteIcon color="primary" fontSize="small" />
            <Typography variant="subtitle1" fontWeight={600}>
              {t('settings.preferencesSection')}
            </Typography>
          </Stack>

          {/* Language */}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <TranslateIcon fontSize="small" color="action" />
            <Typography variant="body2" fontWeight={500}>
              {t('settings.language')}
            </Typography>
          </Stack>
          <TextField
            select
            fullWidth
            size="small"
            value={language}
            onChange={(e) => handleLanguageSelect(e.target.value as Language)}
            helperText={t('settings.languageHelp')}
            sx={{ mb: 3 }}
          >
            {LANGUAGES.map((l) => (
              <MenuItem key={l.code} value={l.code}>
                {l.nativeLabel} — {t(`languages.${l.code}`)}
              </MenuItem>
            ))}
          </TextField>

          {/* Theme */}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <PaletteIcon fontSize="small" color="action" />
            <Typography variant="body2" fontWeight={500}>
              {t('settings.themeMode')}
            </Typography>
          </Stack>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={mode}
            onChange={(_e, value) => value && handleThemeChange(value)}
          >
            <ToggleButton value="light">
              <LightModeIcon fontSize="small" sx={{ mr: 0.5 }} />
              {t('settings.lightMode')}
            </ToggleButton>
            <ToggleButton value="dark">
              <DarkModeIcon fontSize="small" sx={{ mr: 0.5 }} />
              {t('settings.darkMode')}
            </ToggleButton>
          </ToggleButtonGroup>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={pendingLanguage != null}
        title={t('settings.languageChangeTitle')}
        description={
          <>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {t('settings.languageChangeConfirm', { language: pendingLanguageLabel })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('settings.languageChangeConfirmHint')}
            </Typography>
          </>
        }
        confirmLabel={t('settings.languageChangeConfirmAction')}
        severity="warning"
        loading={languageChangeLoading}
        onClose={() => !languageChangeLoading && setPendingLanguage(null)}
        onConfirm={handleLanguageChangeConfirm}
      />
    </Box>
  )
}
